import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Flex, Heading, Text } from '@percent/lemonade'
import type { GroupedDomainAssignee } from '../../../types/permissions.types'
import { AssignRoleModal } from '../AssignRoleModal'
import { DomainAssigneesTable } from '../DomainAssigneesTable'
import { EditAssigneeRolesModal } from '../EditAssigneeRolesModal'
import { RemoveAssigneeModal } from '../RemoveAssigneeModal'
import { groupDomainAssignees } from '../utils'
import type { DomainAssigneesViewProps } from './DomainAssigneesView.types'

type OpenModal =
  | { type: 'assign' }
  | { type: 'editRoles'; assignee: GroupedDomainAssignee }
  | { type: 'remove'; assignee: GroupedDomainAssignee }

export function DomainAssigneesView({
  domain,
  assignees,
  isLoadingAssignees,
  assigneesErrorMessage,
  nextPage,
  previousPage,
  onAssigneesChanged,
  assignRole,
  unassignRole,
  searchAssignees,
  getAssigneeHref,
  canManageAssignee,
}: DomainAssigneesViewProps) {
  const { t } = useTranslation()
  const [openModal, setOpenModal] = useState<OpenModal | null>(null)

  const groupedAssignees = useMemo(
    () => groupDomainAssignees(assignees ?? []),
    [assignees]
  )

  const closeModal = () => setOpenModal(null)

  const renderAssignees = () => {
    if (assigneesErrorMessage) {
      return (
        <Alert variant="error" title={t('rolesAndPermissions.error.title')}>
          {assigneesErrorMessage}
        </Alert>
      )
    }

    return (
      <DomainAssigneesTable
        assignees={groupedAssignees}
        domainRoles={domain.roles}
        isLoading={isLoadingAssignees}
        nextPage={nextPage}
        previousPage={previousPage}
        getAssigneeHref={getAssigneeHref}
        canSetRoles={domain.canSetRoles}
        canManageAssignee={canManageAssignee}
        onEditRoles={(assignee) =>
          setOpenModal({ type: 'editRoles', assignee })
        }
        onRemove={(assignee) => setOpenModal({ type: 'remove', assignee })}
        actionsAndFilters={
          domain.canSetRoles && (
            <Button
              variant="primary"
              onClick={() => setOpenModal({ type: 'assign' })}
              data-testid="assign-role-button"
            >
              {t('rolesAndPermissions.button.assignRole')}
            </Button>
          )
        }
      />
    )
  }

  const renderModal = () => {
    if (!openModal) return null

    switch (openModal.type) {
      case 'assign':
        return (
          <AssignRoleModal
            domainRoles={domain.roles}
            searchAssignees={searchAssignees}
            assignRole={assignRole}
            onAssigned={onAssigneesChanged}
            onClose={closeModal}
          />
        )
      case 'editRoles':
        return (
          <EditAssigneeRolesModal
            domainName={domain.name}
            domainRoles={domain.roles}
            assignee={openModal.assignee}
            assignRole={(roleId) =>
              assignRole({ assigneeId: openModal.assignee.id, roleId })
            }
            unassignRole={(roleId) =>
              unassignRole({ assigneeId: openModal.assignee.id, roleId })
            }
            onChanged={onAssigneesChanged}
            onClose={closeModal}
          />
        )
      case 'remove':
        return (
          <RemoveAssigneeModal
            domainName={domain.name}
            assignee={openModal.assignee}
            unassignRole={(roleId) =>
              unassignRole({ assigneeId: openModal.assignee.id, roleId })
            }
            onRemoved={onAssigneesChanged}
            onClose={closeModal}
          />
        )
    }
  }

  return (
    <Flex direction="column" gap="md">
      <Flex direction="column" gap="xs" data-testid="domain-heading">
        <Heading level="h5">{domain.name}</Heading>
        {domain.description && <Text size="small">{domain.description}</Text>}
      </Flex>
      {renderAssignees()}
      {renderModal()}
    </Flex>
  )
}
